import { useState, useEffect } from "react";
import { api } from "../api";
import type { ClientRow, FieldDef, Identity } from "../types";
import Icon from "../components/Icon";

type SettingsProps = { client: ClientRow | null; identity: Identity };

const EYEBROW: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  fontWeight: 500,
};

type FieldDraft = {
  field_label: string;
  field_type: string;
  options: string;
  required: number;
  show_in_list: number;
  show_in_detail: number;
};

function fdToDraft(fd: FieldDef): FieldDraft {
  return {
    field_label: fd.display_name,
    field_type: fd.field_type,
    options: fd.options ?? "",
    required: fd.required ?? 0,
    show_in_list: fd.show_in_list ?? 0,
    show_in_detail: 1,
  };
}

function FieldDefRow({ fd, onSaved }: { fd: FieldDef; onSaved: (updated: FieldDef) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FieldDraft>(fdToDraft(fd));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.updateFieldDef(fd.id, {
        field_label: draft.field_label,
        field_type: draft.field_type,
        options: draft.options || undefined,
        required: draft.required,
        show_in_list: draft.show_in_list,
        show_in_detail: draft.show_in_detail,
      });
      onSaved({
        ...fd,
        display_name: draft.field_label,
        field_type: draft.field_type,
        options: draft.options || null,
        required: draft.required,
        show_in_list: draft.show_in_list,
      });
      setEditing(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(fdToDraft(fd));
    setEditing(false);
    setError(null);
  }

  if (!editing) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "180px 100px 80px 60px 60px 1fr auto",
          gap: "0 16px",
          alignItems: "center",
          padding: "12px 0",
          borderBottom: "1px solid var(--rule-faint)",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 520 }}>{fd.display_name}</div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-muted)" }}>
          {fd.field_name}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-muted)" }}>
          {fd.field_type}
        </div>
        <div
          style={{
            fontSize: 11,
            fontFamily: "var(--font-mono)",
            color: fd.show_in_list ? "var(--accent)" : "var(--fg-subtle)",
          }}
        >
          {fd.show_in_list ? "listed" : "—"}
        </div>
        <div
          style={{
            fontSize: 11,
            fontFamily: "var(--font-mono)",
            color: fd.required ? "var(--warm-bright)" : "var(--fg-subtle)",
          }}
        >
          {fd.required ? "req" : "—"}
        </div>
        <div style={{ fontSize: 12, color: "var(--fg-subtle)", fontFamily: "var(--font-mono)" }}>
          {fd.options ? fd.options.slice(0, 40) + (fd.options.length > 40 ? "…" : "") : ""}
        </div>
        <button
          className="btn btn-quiet btn-sm"
          onClick={() => { setEditing(true); setDraft(fdToDraft(fd)); }}
          style={{ fontSize: 12 }}
        >
          <Icon name="pencil" size={12} /> Edit
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--rule)",
        borderRadius: "var(--r-sm)",
        padding: "14px",
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px 16px",
          marginBottom: 10,
        }}
      >
        <div>
          <div style={{ ...EYEBROW, marginBottom: 4 }}>Display label</div>
          <input
            className="input"
            value={draft.field_label}
            onChange={(e) => setDraft((d) => ({ ...d, field_label: e.target.value }))}
          />
        </div>
        <div>
          <div style={{ ...EYEBROW, marginBottom: 4 }}>Field type</div>
          <select
            className="input"
            value={draft.field_type}
            onChange={(e) => setDraft((d) => ({ ...d, field_type: e.target.value }))}
          >
            {["text", "textarea", "select", "boolean", "number", "date", "url"].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ ...EYEBROW, marginBottom: 4 }}>Options (JSON array, for select type)</div>
          <input
            className="input input-mono"
            value={draft.options}
            onChange={(e) => setDraft((d) => ({ ...d, options: e.target.value }))}
            placeholder='["Option A","Option B"]'
            style={{ fontSize: 12 }}
          />
        </div>
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={draft.show_in_list === 1}
              onChange={(e) => setDraft((d) => ({ ...d, show_in_list: e.target.checked ? 1 : 0 }))}
            />
            <span style={{ fontSize: 13 }}>Show in catalog list</span>
          </label>
        </div>
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={draft.required === 1}
              onChange={(e) => setDraft((d) => ({ ...d, required: e.target.checked ? 1 : 0 }))}
            />
            <span style={{ fontSize: 13 }}>Required</span>
          </label>
        </div>
      </div>
      {error && (
        <div style={{ marginBottom: 8, fontSize: 12, fontFamily: "var(--font-mono)", color: "#e58a6d" }}>
          {error}
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-primary btn-sm" onClick={() => void save()} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="btn btn-quiet btn-sm" onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function Settings({ client, identity }: SettingsProps) {
  const isPlatformOwner = identity.role === "platform_owner";
  const [fieldDefs, setFieldDefs] = useState<FieldDef[]>([]);
  const [fdLoading, setFdLoading] = useState(false);
  const [fdError, setFdError] = useState<string | null>(null);

  useEffect(() => {
    if (!client || !isPlatformOwner) return;
    setFdLoading(true);
    setFdError(null);
    api
      .getFieldDefs(client.id)
      .then((res) => setFieldDefs(res.field_defs))
      .catch((e: unknown) => setFdError(e instanceof Error ? e.message : String(e)))
      .finally(() => setFdLoading(false));
  }, [client, isPlatformOwner]);

  if (!client) return null;

  function onFieldDefSaved(updated: FieldDef) {
    setFieldDefs((prev) => prev.map((fd) => (fd.id === updated.id ? updated : fd)));
  }

  return (
    <div className="page page-enter">
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <div className="section-head">
          <div>
            <span className="eyebrow">For {client.name}</span>
            <h2 className="t-h2" style={{ marginTop: 10, fontSize: 32 }}>Settings.</h2>
          </div>
        </div>

        {/* Client record (read-only) */}
        <div className="settings-section">
          <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "2px solid var(--fg)" }}>
            <h4 className="t-h4">Client record</h4>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4 }}>
              Read-only · managed in D1
            </div>
          </div>
          <div className="settings-row">
            <div className="label">Name<div className="help">Display name for this client</div></div>
            <div className="control"><input className="input" readOnly value={client.name} /></div>
          </div>
          <div className="settings-row">
            <div className="label">Slug<div className="help">Used in API paths</div></div>
            <div className="control"><input className="input input-mono" readOnly value={client.slug} /></div>
          </div>
          <div className="settings-row">
            <div className="label">Status</div>
            <div className="control"><input className="input input-mono" readOnly value={client.status ?? "—"} /></div>
          </div>
          <div className="settings-row">
            <div className="label">CF account hash<div className="help">Cloudflare Images identifier</div></div>
            <div className="control"><input className="input input-mono" readOnly value={client.cf_account_hash ?? "—"} /></div>
          </div>
        </div>

        {/* Field definitions — platform_owner only. Schema-shaping is a Mark-level
            action and should not appear inside a client workspace. */}
        {isPlatformOwner && (
        <div style={{ marginTop: 32 }}>
          <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "2px solid var(--fg)" }}>
            <h4 className="t-h4">Field definitions</h4>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4 }}>
              Edit label, type, options, and list visibility · field_name is immutable
            </div>
          </div>

          {fdError && (
            <div
              role="alert"
              style={{
                padding: "10px 14px",
                background: "rgba(229,138,109,0.10)",
                border: "1px solid rgba(229,138,109,0.35)",
                borderRadius: "var(--r-md)",
                color: "#e58a6d",
                marginBottom: 16,
                fontFamily: "var(--font-mono)",
                fontSize: 12,
              }}
            >
              {fdError}
            </div>
          )}

          {fdLoading ? (
            <div>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="skeleton"
                  style={{ height: 44, marginBottom: 4, borderRadius: "var(--r-sm)" }}
                />
              ))}
            </div>
          ) : fieldDefs.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--fg-subtle)", fontStyle: "italic", padding: "20px 0" }}>
              No field definitions found. Create an asset type via the API to add fields.
            </div>
          ) : (
            <div>
              {/* Column header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "180px 100px 80px 60px 60px 1fr auto",
                  gap: "0 16px",
                  padding: "6px 0 8px",
                  borderBottom: "1px solid var(--rule-strong)",
                }}
              >
                {["Display name", "field_name", "Type", "List", "Req", "Options", ""].map(
                  (h) => (
                    <div key={h} style={EYEBROW}>{h}</div>
                  )
                )}
              </div>

              {fieldDefs.map((fd) => (
                <FieldDefRow key={fd.id} fd={fd} onSaved={onFieldDefSaved} />
              ))}

              <div
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  background: "var(--surface)",
                  border: "1px dashed var(--rule-strong)",
                  borderRadius: "var(--r-md)",
                  fontSize: 12,
                  color: "var(--fg-muted)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                Creating new field definitions requires a GET /asset-types endpoint (not yet in
                the audit). Surface to SP when ready.
              </div>
            </div>
          )}
        </div>
        )}

        {/* The site (design shell, OD-2 — wired when the client-settings endpoint lands) */}
        <ShellSection
          title="The site"
          subtitle={isPlatformOwner ? "Public URL, brand kit, primary contact" : "Your public URL and contact details"}
          note={
            isPlatformOwner
              ? "The client's production URL, brand-kit reference, and primary contact will be managed here. Wired once the client-settings endpoint lands; today these live in D1."
              : "Your production URL, brand kit, and primary contact will be managed here. (Coming soon.)"
          }
        />

        {/* Who has access (design shell) — Gate D in v1.1. Client roles see a
            short coming-soon line; platform_owner sees the implementation note. */}
        <ShellSection
          title="Who has access"
          subtitle={isPlatformOwner ? "Team members and roles" : "Your team and their access"}
          note={
            isPlatformOwner
              ? "Team members with their roles (owner, editor, viewer) and pending invites will appear here. Wired once the user-management endpoint lands; identities are seeded directly in D1 for now."
              : "Manage who can access this workspace. (Coming soon.)"
          }
        />
      </div>
    </div>
  );
}

function ShellSection({ title, subtitle, note }: { title: string; subtitle: string; note: string }) {
  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "2px solid var(--fg)" }}>
        <h4 className="t-h4">{title}</h4>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4 }}>
          {subtitle}
        </div>
      </div>
      <div
        style={{
          padding: "12px 14px",
          background: "var(--surface)",
          border: "1px dashed var(--rule-strong)",
          borderRadius: "var(--r-md)",
          fontSize: 12,
          color: "var(--fg-muted)",
          fontFamily: "var(--font-mono)",
          lineHeight: 1.6,
        }}
      >
        {note}
      </div>
    </div>
  );
}
